import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { fetchWeeklyUserAnalytics } from "../../api/user";

const weeks = [
  { label: "Week 1", startDay: 1 },
  { label: "Week 2", startDay: 8 },
  { label: "Week 3", startDay: 15 },
  { label: "Week 4", startDay: 22 },
  { label: "Week 5", startDay: 29 },
];

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

/**
 * WeeklyUsers
 * Props:
 *   apiUrl (string) - endpoint to fetch weekly users. Default: '/api/weekly-users'
 *
 * Endpoint expected query params: ?year=YYYY&month=MM&week=Week%201
 * Supported response shapes:
 *   1) [{ date: '2025-11-01', value: 120 }, ...]
 *   2) { data: [{ date: '2025-11-01', value: 120 }, ...] }
 */
const WeeklyUsers = () => {
  const [selectedWeek, setSelectedWeek] = useState(weeks[0].label);
  const [selectedMonth, setSelectedMonth] = useState(
    months[new Date().getMonth()]
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const filter = useSelector((state) => state.filter);
  const filterWithoutUTM = useMemo(() => {
    const { utm_options, ...rest } = filter;
    return rest;
  }, [JSON.stringify(filter)]);

  const [data, setData] = useState([]); // Highcharts data: [ [timestamp, value], ... ]
  const [onlineUsersCount, setOnlineUsersCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

useEffect(() => {
  // Build request params
  const monthIndex = months.indexOf(selectedMonth) + 1; // 1-12
  const params = new URLSearchParams({
    year: String(selectedYear),
    month: String(monthIndex),
    week: selectedWeek, // e.g., "Week 1"
  });

  const controller = new AbortController();
  const signal = controller.signal;

  async function fetchWeekData() {
    setLoading(true);
    setError(null);
    try {
      const response  = await fetchWeeklyUserAnalytics(params.toString()); // fetchWeeklyUserAnalytics
      const payload = response?.data ?? response;

      const onlineCount = payload?.pastOnlineUsersCount.reduce((acc, item) => acc + (item.activeUsersCount ?? item.count), 0) ?? 0;
      const slots = payload?.pastOnlineUsersCount ?? [];
      const mapped = slots.map((item) => {
        const ts = Date.parse(item.date);
        return [ts, Number(item.activeUsersCount ?? item.count ?? 0)];
      });
      setData(mapped);
      setOnlineUsersCount(onlineCount);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('WeeklyUsers fetch error:', err);
      setError(err.message || 'Failed to load weekly data');
    } finally {
      setLoading(false);
    }
  }

  fetchWeekData();

  return () => controller.abort();
}, [selectedWeek, selectedMonth, selectedYear]);

  return (
    <Box sx={{ p: 3, border: "1px solid #ccc", borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: "20px", color: "#000" }}>
          Weekly Online Users
        </Typography>

        {/* Dropdowns */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {/* Week */}
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="week-select-label">Week</InputLabel>
            <Select
              labelId="week-select-label"
              value={selectedWeek}
              label="Week"
              onChange={(e) => setSelectedWeek(e.target.value)}
              sx={{
                height: 40,
                "& .MuiSelect-select": {
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              {weeks.map((week) => (
                <MenuItem key={week.label} value={week.label}>
                  {week.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Month */}
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{
                height: 40,
                "& .MuiSelect-select": {
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              {months.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Year */}
          <FormControl sx={{ minWidth: 100 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                height: 40,
                "& .MuiSelect-select": {
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
              }}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Loading / Error */}
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography>Loading weekly users…</Typography>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Chart */}
      {
        onlineUsersCount
          ?
          <HighchartsReact
            highcharts={Highcharts}
            options={{
              chart: { backgroundColor: "#fff" },
              title: {
                text: null,
                align: "left",
                verticalAlign: "top",
                x: 0,
                y: 10,
                style: { fontSize: "16px", fontWeight: "bold", color: "#000" },
              },
              subtitle: {
                text: onlineUsersCount,
                align: "left",
                verticalAlign: "top",
                x: 0,
                y: 40,
                style: {
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#000",
                  textAlign: "left",
                  textTransform: "uppercase",
                },
              },
              credits: { enabled: false },
              xAxis: {
                type: "datetime",
                opposite: true,
                labels: {
                  align: "center",
                  y: -10,
                  formatter() {
                    return Highcharts.dateFormat("%a %d", this.value); // E.g., Mon 01
                  },
                },
                lineWidth: 0,
              },
              yAxis: {
                title: { text: "" },
                labels: { enabled: false },
                gridLineWidth: 0,
              },
              legend: { enabled: false },
              tooltip: {
                valueSuffix: " Users",
                xDateFormat: "%A, %b %e, %Y",
              },
              plotOptions: {
                series: {
                  marker: {
                    radius: 5,
                    fillColor: "#ffffff",
                    lineWidth: 2,
                    lineColor: "#008E40",
                  },
                  lineWidth: 4,
                  shadow: true,
                },
              },
              series: [
                {
                  type: "area",
                  name: "Weekly Online Users",
                  data: data,
                  color: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                      [0, "#008E40"],
                      [1, "#008E40"],
                    ],
                  },
                  fillColor: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                      [0, "rgba(0, 142, 64, 0.7)"],
                      [1, "rgba(217, 217, 217, 0)"],
                    ],
                  },
                },
              ],
            }}
          />
          : <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No data available
            </Typography>
          </Box>
      }
    </Box>
  );
};

export default WeeklyUsers;
